/**
 * SAM.gov Configuration Service - Admin operations for SAM.gov settings
 */
import {
  collection,
  doc,
  getDoc,
  setDoc,
  getDocs,
  query,
  orderBy,
  limit,
  serverTimestamp,
} from "firebase/firestore";
import { db } from "../../../config/firebase";

const CONFIG_COLLECTION = "sam_config";
const INGEST_RUNS_COLLECTION = "ingest_runs";

/**
 * Get SAM.gov configuration
 */
export async function getSamConfig() {
  const docRef = doc(db, CONFIG_COLLECTION, "settings");
  const snapshot = await getDoc(docRef);

  if (!snapshot.exists()) {
    // Return defaults
    return {
      naics_codes: [],
      historical_window_years: 5,
      similarity_thresholds: {
        high: 0.7,
        medium: 0.4,
      },
      ingest_schedule: "daily",
      last_updated: null,
    };
  }

  return snapshot.data();
}

/**
 * Update SAM.gov configuration
 */
export async function updateSamConfig(config) {
  const docRef = doc(db, CONFIG_COLLECTION, "settings");
  await setDoc(docRef, {
    ...config,
    last_updated: serverTimestamp(),
  }, { merge: true });
}

/**
 * Get configured NAICS codes
 */
export async function getConfiguredNaicsCodes() {
  const config = await getSamConfig();
  return config.naics_codes || [];
}

/**
 * Update configured NAICS codes
 */
export async function setConfiguredNaicsCodes(naicsCodes) {
  await updateSamConfig({ naics_codes: naicsCodes });
}

/**
 * Get recent ingest runs
 */
export async function getRecentIngestRuns(maxResults = 10) {
  const runsRef = collection(db, INGEST_RUNS_COLLECTION);
  const q = query(
    runsRef,
    orderBy("started_at", "desc"),
    limit(maxResults)
  );

  const snapshot = await getDocs(q);
  return snapshot.docs.map((doc) => ({
    id: doc.id,
    ...doc.data(),
  }));
}

/**
 * Get the latest ingest run
 */
export async function getLatestIngestRun() {
  const runs = await getRecentIngestRuns(1);
  return runs[0] || null;
}

/**
 * Format timestamp for display
 */
export function formatTimestamp(timestamp) {
  if (!timestamp) return "Never";

  const date = timestamp.toDate ? timestamp.toDate() : new Date(timestamp);

  return new Intl.DateTimeFormat("en-US", {
    year: "numeric",
    month: "short",
    day: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  }).format(date);
}

/**
 * Format duration in ms to human readable
 */
export function formatDuration(ms) {
  if (!ms) return "N/A";

  if (ms < 1000) return `${ms}ms`;
  if (ms < 60000) return `${(ms / 1000).toFixed(1)}s`;
  return `${(ms / 60000).toFixed(1)}m`;
}

/**
 * Common NAICS codes for government contracting
 */
export const COMMON_NAICS_CODES = [
  { code: "541511", description: "Custom Computer Programming Services" },
  { code: "541512", description: "Computer Systems Design Services" },
  { code: "541513", description: "Computer Facilities Management Services" },
  { code: "541519", description: "Other Computer Related Services" },
  { code: "541611", description: "Administrative Management Consulting" },
  { code: "541612", description: "Human Resources Consulting" },
  { code: "541613", description: "Marketing Consulting" },
  { code: "541614", description: "Process & Logistics Consulting" },
  { code: "541618", description: "Other Management Consulting" },
  { code: "541690", description: "Other Scientific & Technical Consulting" },
  { code: "541715", description: "R&D in Physical, Engineering & Life Sciences" },
  { code: "518210", description: "Data Processing & Hosting Services" },
  { code: "561110", description: "Office Administrative Services" },
  { code: "561210", description: "Facilities Support Services" },
  { code: "561320", description: "Temporary Help Services" },
  { code: "561330", description: "Professional Employer Organizations" },
  { code: "611430", description: "Professional & Management Training" },
];
